/**
 * Campagnes partenaires : éligibilité (zones ou geo URL) → window.LovenSponsored + Handles.
 * Grille : ventilation native ads (organic d’abord, puis slots espacés — pas de bloc en tête).
 * Consommé par loven-sponsored-badges.js, collection-map.js et collection-availability-filter.js.
 */
(function () {
  'use strict';

  var REGISTRY_ID = 'loven-partner-ads-registry-json';

  /**
   * Bridge Supabase — campagne_partenaire.
   * Filtre éligibilité (produit ∈ collection ∧ zone match) appliqué côté backend.
   * Évite la limite Shopify Liquid de 50 metaobjects par page.
   */
  var PARTNER_CAMPAIGNS_API =
    'https://igthraqyjiadzclkkixn.supabase.co/functions/v1/partner-campaigns';
  var PARTNER_CAMPAIGNS_CACHE_TTL_MS = 60 * 1000;
  var PARTNER_CAMPAIGNS_FETCH_TIMEOUT_MS = 4000;

  /** Dernier registre publié (Supabase ou inline). Permet de re-publier sans refetcher. */
  var __lovenLastRegistry = null;

  function readRegistry() {
    var el = document.getElementById(REGISTRY_ID);
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  /**
   * Clé cache sessionStorage. Forme stable :
   *   loven:partner-campaigns:col:<handle>:<id>    (mode collection)
   *   loven:partner-campaigns:geo:<lat>:<lon>:<r>  (mode geo /search)
   */
  function partnerCampaignsCacheKey(payload) {
    if (payload && payload.dest_lat != null) {
      return (
        'loven:partner-campaigns:geo:' +
        payload.dest_lat +
        ':' +
        payload.dest_lon +
        ':' +
        (payload.dest_radius_km != null ? payload.dest_radius_km : 50)
      );
    }
    return (
      'loven:partner-campaigns:col:' +
      (payload && payload.collection_handle ? payload.collection_handle : '') +
      ':' +
      (payload && payload.collection_id ? payload.collection_id : '')
    );
  }

  function readPartnerCampaignsCache(payload) {
    try {
      if (!window.sessionStorage) return null;
      var raw = window.sessionStorage.getItem(partnerCampaignsCacheKey(payload));
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.at) return null;
      if (Date.now() - entry.at > PARTNER_CAMPAIGNS_CACHE_TTL_MS) return null;
      return entry.data || null;
    } catch (e) {
      return null;
    }
  }

  function writePartnerCampaignsCache(payload, data) {
    try {
      if (!window.sessionStorage) return;
      window.sessionStorage.setItem(
        partnerCampaignsCacheKey(payload),
        JSON.stringify({ at: Date.now(), data: data })
      );
    } catch (e) {}
  }

  function clearPartnerCampaignsCacheByPayload(payload) {
    try {
      if (!window.sessionStorage) return;
      window.sessionStorage.removeItem(partnerCampaignsCacheKey(payload));
    } catch (e) {}
  }

  /**
   * POST partner-campaigns avec timeout + cache sessionStorage.
   * payload doit contenir SOIT collection_handle (mode collection),
   * SOIT dest_lat + dest_lon (mode geo /search).
   * callback(data, meta) — data null si erreur, meta = { source, durationMs, error? }.
   */
  function fetchPartnerCampaignsByPayload(payload, callback) {
    if (
      !payload ||
      (!payload.collection_handle && (payload.dest_lat == null || payload.dest_lon == null))
    ) {
      callback(null, { source: 'noop', reason: 'no-payload', durationMs: 0 });
      return;
    }
    var cached = readPartnerCampaignsCache(payload);
    if (cached) {
      callback(cached, { source: 'cache', durationMs: 0 });
      return;
    }
    var startedAt = Date.now();
    var controller = null;
    var timeoutTimer = null;
    try {
      if (typeof AbortController === 'function') {
        controller = new AbortController();
        timeoutTimer = setTimeout(function () {
          try {
            controller.abort();
          } catch (e) {}
        }, PARTNER_CAMPAIGNS_FETCH_TIMEOUT_MS);
      }
    } catch (e) {}

    var fetchInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    };
    if (controller) fetchInit.signal = controller.signal;

    fetch(PARTNER_CAMPAIGNS_API, fetchInit)
      .then(function (r) {
        if (!r.ok) throw new Error('http-' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (timeoutTimer) clearTimeout(timeoutTimer);
        writePartnerCampaignsCache(payload, data);
        callback(data, { source: 'network', durationMs: Date.now() - startedAt });
      })
      .catch(function (err) {
        if (timeoutTimer) clearTimeout(timeoutTimer);
        callback(null, {
          source: 'error',
          durationMs: Date.now() - startedAt,
          error: (err && err.message) || String(err),
        });
      });
  }

  /** Helpers de signature historique — restent supportés. */
  function fetchPartnerCampaigns(handle, id, callback) {
    fetchPartnerCampaignsByPayload(
      {
        collection_handle: handle || '',
        collection_id: id != null ? String(id) : '',
      },
      callback
    );
  }
  function fetchPartnerCampaignsGeo(lat, lon, radiusKm, callback) {
    fetchPartnerCampaignsByPayload(
      {
        dest_lat: lat,
        dest_lon: lon,
        dest_radius_km: radiusKm != null ? radiusKm : 50,
      },
      callback
    );
  }

  /** Construit un registre virtuel au format attendu par publishSponsored / logRegistrySummary. */
  function buildVirtualRegistryFromApi(inlineCtx, apiData) {
    var apiCtx = (apiData && apiData.context) || {};
    var campaigns = (apiData && apiData.campaigns) || [];
    var mode = apiCtx.mode || 'collection';
    return {
      context: {
        collectionHandle: inlineCtx.collectionHandle || apiCtx.collection_handle || '',
        collectionId:
          inlineCtx.collectionId != null
            ? inlineCtx.collectionId
            : apiCtx.collection_id != null
              ? apiCtx.collection_id
              : null,
        collectionTitle: inlineCtx.collectionTitle || apiCtx.collection_title || '',
        collectionProductCount: apiCtx.collection_product_count || 0,
        metaobjectTotal: campaigns.length,
        collectionProductHandles: [],
        collectionProductIds: [],
        source: 'supabase',
        mode: mode,
        generatedAt: apiCtx.generated_at || null,
        filtersApplied: apiCtx.filters_applied || [],
        destLat: apiCtx.dest_lat != null ? apiCtx.dest_lat : null,
        destLon: apiCtx.dest_lon != null ? apiCtx.dest_lon : null,
        destRadiusKm: apiCtx.dest_radius_km != null ? apiCtx.dest_radius_km : null,
      },
      campaigns: campaigns,
      __backendFiltered: true,
    };
  }

  function readDestSearchFromUrl() {
    try {
      var p = new URLSearchParams(window.location.search);
      var la = p.get('dest_lat');
      var lo = p.get('dest_lon');
      if (!la || !lo) return null;
      var plat = parseFloat(la, 10);
      var plng = parseFloat(lo, 10);
      if (isNaN(plat) || isNaN(plng)) return null;
      var fb =
        typeof window.__lovenReadDestRadiusFallbackKm === 'function'
          ? window.__lovenReadDestRadiusFallbackKm()
          : 50;
      var r = parseFloat(p.get('dest_radius_km') || String(fb), 10);
      if (isNaN(r) || r <= 0) r = fb;
      if (r === 80) r = 50;
      r = Math.min(500, Math.max(5, r));
      return { lat: plat, lng: plng, radiusKm: r };
    } catch (e) {
      return null;
    }
  }

  function haversineKm(lat1, lon1, lat2, lon2) {
    var R = 6371;
    function toRad(d) {
      return (d * Math.PI) / 180;
    }
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function todayYmd() {
    var d = new Date();
    var m = String(d.getMonth() + 1);
    var day = String(d.getDate());
    if (m.length < 2) m = '0' + m;
    if (day.length < 2) day = '0' + day;
    return d.getFullYear() + '-' + m + '-' + day;
  }

  function passesActifDates(c, filterOn, today) {
    if (!filterOn) return true;
    if (c.actif === false) return false;
    if (c.dateDebut && c.dateDebut > today) return false;
    if (c.dateFin && c.dateFin < today) return false;
    return true;
  }

  function normZoneHandle(h) {
    return String(h || '')
      .toLowerCase()
      .trim();
  }

  function getCollectionHandleFromPath() {
    var m = (window.location.pathname || '').match(/\/collections\/([^/?#]+)/i);
    return m ? String(m[1] || '').toLowerCase() : '';
  }

  /** Collection catalogue : grille statique visible (pas search, pas filtre dates / geo API). */
  function ensureCollectionUsesStaticGrid() {
    if (getCollectionHandleFromPath() === 'search') return;
    if (typeof window.LovenIsNamedCollectionCatalogPage === 'function' && window.LovenIsNamedCollectionCatalogPage()) {
      if (typeof window.LovenRunNamedCollectionCatalogBrowse === 'function') {
        window.LovenRunNamedCollectionCatalogBrowse();
      }
      return;
    }
    if (window.__lovenZoneCollectionGeoBrowse || window.__lovenDynamicAvailabilityMode) return;
    if (
      typeof window.LovenIsZoneCollectionGeoBrowse === 'function' &&
      window.LovenIsZoneCollectionGeoBrowse()
    ) {
      return;
    }
    try {
      var p = new URLSearchParams(window.location.search);
      if (p.get('start') && p.get('end')) return;
    } catch (e) {
      return;
    }
    var root = document.getElementById('CollectionAjaxResult');
    if (root) root.classList.remove('loven-dynamic-availability-mode');
    window.__lovenDynamicAvailabilityMode = false;
    if (typeof window.LovenDynamicAvailabilityClear === 'function') {
      window.LovenDynamicAvailabilityClear();
    }
  }

  /**
   * Règle métier collection (source de vérité unique côté JS) :
   *   campagne éligible = (produit ∈ collection.products) ET (collection courante ∈ campagne.zones)
   * Toute la normalisation se fait ici (handles lowercase, ids stringifiés).
   */
  function buildEligibilityIndex(registry) {
    var ctx = (registry && registry.context) || {};
    var prodHandles = {};
    var prodIds = {};
    (ctx.collectionProductHandles || []).forEach(function (h) {
      var k = normZoneHandle(h);
      if (k) prodHandles[k] = true;
    });
    (ctx.collectionProductIds || []).forEach(function (id) {
      var k = String(id || '').trim();
      if (k) prodIds[k] = true;
    });
    return {
      collectionHandle: normZoneHandle(ctx.collectionHandle),
      collectionId: ctx.collectionId != null ? String(ctx.collectionId) : '',
      prodHandles: prodHandles,
      prodIds: prodIds,
    };
  }

  function isProductInCollection(c, idx) {
    var hk = normZoneHandle(c.productHandle);
    if (hk && idx.prodHandles[hk]) return true;
    var pid = c.productId != null ? String(c.productId) : '';
    if (pid && idx.prodIds[pid]) return true;
    return false;
  }

  function zoneMatchesCurrentCollection(c, idx) {
    var i;
    if (!idx.collectionHandle && !idx.collectionId) return false;
    var zh = c.zoneHandles || [];
    for (i = 0; i < zh.length; i++) {
      if (idx.collectionHandle && normZoneHandle(zh[i]) === idx.collectionHandle) return true;
    }
    var zi = c.zoneIds || [];
    for (i = 0; i < zi.length; i++) {
      if (idx.collectionId && String(zi[i]) === idx.collectionId) return true;
    }
    return false;
  }

  function isCollectionCampaignEligible(c, idx) {
    return isProductInCollection(c, idx) && zoneMatchesCurrentCollection(c, idx);
  }

  /** Search : filtre geo API. Collection : pas de filtre API (catalogue = collection.products). */
  function filterCampaignsToAvailabilityRows(campaigns, isSearch) {
    return campaigns;
  }

  function eligibleForSearch(c, dest, radiusKm) {
    if (c.lat == null || c.lon == null || isNaN(Number(c.lat)) || isNaN(Number(c.lon))) return false;
    return haversineKm(dest.lat, dest.lng, Number(c.lat), Number(c.lon)) <= radiusKm;
  }

  function pickBestPerProduct(campaigns) {
    var byProduct = {};
    campaigns.forEach(function (c) {
      var pid = c.productId;
      var cur = byProduct[pid];
      if (!cur) {
        byProduct[pid] = c;
        return;
      }
      var zc = c.zoneCount || 0;
      var zcCur = cur.zoneCount || 0;
      if (zc > zcCur || (zc === zcCur && (c.priorite || 0) > (cur.priorite || 0))) {
        byProduct[pid] = c;
      }
    });
    return Object.keys(byProduct).map(function (k) {
      return byProduct[k];
    });
  }

  function readGeoDestForEligibility(registry) {
    if (typeof window.LovenGetCollectionGeoDest === 'function') {
      return window.LovenGetCollectionGeoDest();
    }
    return readDestSearchFromUrl();
  }

  function getEligibleCampaigns(registry, collectionHandle) {
    if (!registry || !registry.campaigns || !registry.campaigns.length) return [];
    var today = todayYmd();
    var filterDates = !!registry.filterActifDates;
    var ctx = registry.context || {};
    var ctxHandle = ctx.collectionHandle || collectionHandle || '';
    var isSearch = ctxHandle === 'search';
    var dest = isSearch ? readGeoDestForEligibility(registry) : null;
    var radiusKm =
      dest && dest.radiusKm != null
        ? dest.radiusKm
        : registry.searchRadiusKm != null
          ? registry.searchRadiusKm
          : 50;
    var idx = isSearch ? null : buildEligibilityIndex(registry);

    var eligible = [];
    registry.campaigns.forEach(function (c) {
      if (!passesActifDates(c, filterDates, today)) return;
      if (isSearch) {
        if (!dest) return;
        if (!eligibleForSearch(c, dest, radiusKm)) return;
      } else {
        if (!isCollectionCampaignEligible(c, idx)) return;
      }
      eligible.push(c);
    });

    eligible = pickBestPerProduct(eligible);
    return filterCampaignsToAvailabilityRows(eligible, isSearch);
  }

  /**
   * Wrapper unifié : si le registre a été pré-filtré par le backend Supabase
   * (campagnes éligibles directement renvoyées par /partner-campaigns), on
   * applique uniquement passesActifDates + pickBestPerProduct.
   * Sinon, on reprend le chemin classique (filtrage zones / geo côté JS).
   */
  function getEligibleCampaignsFromRegistry(registry, collectionHandle) {
    if (!registry || !registry.campaigns) return [];
    if (registry.__backendFiltered) {
      var today = todayYmd();
      var filterDates = !!registry.filterActifDates;
      var filtered = registry.campaigns.filter(function (c) {
        return passesActifDates(c, filterDates, today);
      });
      return pickBestPerProduct(filtered);
    }
    return getEligibleCampaigns(registry, collectionHandle);
  }

  function isAdsDebugOn() {
    try {
      return new URLSearchParams(window.location.search).get('loven_ads_debug') === '1';
    } catch (e) {
      return false;
    }
  }

  function shuffleArray(arr) {
    var a = arr.slice();
    var i;
    var j;
    var t;
    for (i = a.length - 1; i > 0; i--) {
      j = Math.floor(Math.random() * (i + 1));
      t = a[i];
      a[i] = a[j];
      a[j] = t;
    }
    return a;
  }

  /**
   * Ordre bandeau : priorité décroissante, puis tirage aléatoire dans chaque palier.
   * Le set affiché est figé par hash filtres (Rule 4) — pas de re-tirage au pan/zoom.
   */
  function campaignsInStoredOrder(campaigns, storedOrder) {
    if (!storedOrder || !storedOrder.length) return campaigns || [];
    var byHandle = {};
    var byId = {};
    var used = {};
    var out = [];
    var rest = [];
    var item;
    var h;
    var id;
    var c;
    (campaigns || []).forEach(function (camp) {
      if (camp.productHandle) byHandle[normZoneHandle(camp.productHandle)] = camp;
      if (camp.productId != null) byId[String(camp.productId)] = camp;
    });
    storedOrder.forEach(function (entry) {
      if (entry && typeof entry === 'object') {
        h = entry.productHandle;
        id = entry.productId;
      } else {
        h = entry;
        id = entry;
      }
      c =
        (h && byHandle[normZoneHandle(h)]) || (id != null && byId[String(id)]) || null;
      if (!c || used[String(c.productId)]) return;
      used[String(c.productId)] = true;
      out.push(c);
    });
    (campaigns || []).forEach(function (camp) {
      if (!used[String(camp.productId)]) rest.push(camp);
    });
    return out.concat(rest);
  }

  /**
   * Positions fixes en grille (1-based) — pas de modulo.
   * Hero @1, re-engagement @6, 3e slot @12 si >40 résultats (search).
   */
  // Quatre créneaux, alignés sur la capacité vendue (4 places par page).
  // Arbitrage Florian 15/09/2026 : avant, [1, 6, 12] — le 4e sponsorisé
  // n'avait pas de créneau et retombait en fin de liste.
  var SPONSORED_GRID_SLOTS_1_BASED = [1, 6, 8, 12];

  var COLLECTION_SPONSORED_MAX = 5;
  var SEARCH_SPONSORED_THRESHOLD = 40;
  var SEARCH_SPONSORED_MAX_UNDER = 2;
  var SEARCH_SPONSORED_MAX_OVER = 3;
  var __lovenDisplayPick = null;
  var __lovenDisplayPickMax = null;
  var __lovenLastFilterHash = null;
  /** Rule 4 : tirage en mémoire (pan/zoom) — pas de sessionStorage (F5 = nouveau tirage). */
  var __lovenDisplayPickCommitted = false;
  /** Fetch partner-campaigns terminé — évite de figer un tirage vide avant l’API (race search). */
  var __lovenPartnerCampaignsSettled = false;

  function isZoneCollectionBrowsePage() {
    if (getCollectionHandleFromPath() === 'search') return false;
    if (typeof window.LovenIsZoneCollectionGeoBrowse === 'function') {
      return window.LovenIsZoneCollectionGeoBrowse();
    }
    return false;
  }

  function roundFilterCoord(n) {
    var v = Number(n);
    if (!isFinite(v)) return '';
    return String(Math.round(v * 10000) / 10000);
  }

  /**
   * Rule 4 — hash stable des filtres actifs (collection + search).
   * Nouveau tirage si le hash change ; persistance en mémoire uniquement (pan/zoom, pas F5).
   */
  function buildActiveFiltersHash() {
    var parts = [];
    var p;
    try {
      p = new URLSearchParams(window.location.search);
    } catch (e) {
      return 'unknown';
    }

    var handle = getCollectionHandleFromPath();
    parts.push('page:' + (handle || ''));

    var reg = __lovenLastRegistry || readRegistry();
    var cid = reg && reg.context && reg.context.collectionId;
    if (cid != null) parts.push('cid:' + cid);

    var dest = readDestSearchFromUrl();
    if (dest) {
      parts.push(
        'geo:' +
          roundFilterCoord(dest.lat) +
          ',' +
          roundFilterCoord(dest.lng) +
          ',' +
          Math.round(dest.radiusKm)
      );
    }

    var start = p.get('start');
    var end = p.get('end');
    if (start) parts.push('start:' + start);
    if (end) parts.push('end:' + end);

    var sort = p.get('sort_by');
    if (sort) parts.push('sort:' + sort);

    var q = p.get('q');
    if (q) parts.push('q:' + q);

    var filterKeys = [];
    p.forEach(function (val, key) {
      if (key.indexOf('filter.') === 0) filterKeys.push(key + '=' + val);
    });
    filterKeys.sort();
    if (filterKeys.length) parts.push('filters:' + filterKeys.join('&'));

    parts.push('max:' + getDisplaySponsoredMax());

    return parts.join('|');
  }

  function buildEmptySponsoredPick(max) {
    return {
      ids: [],
      handles: [],
      campaigns: [],
      order: [],
      max: max,
      committedEmpty: true,
    };
  }

  function isPickCommittedEmpty(pick) {
    return !!(pick && (pick.committedEmpty || !(pick.ids && pick.ids.length)));
  }

  function finalizeSponsoredPick(pick, max) {
    var empty = isPickCommittedEmpty(pick);
    if (empty && !__lovenPartnerCampaignsSettled) {
      __lovenDisplayPick = pick;
      __lovenDisplayPickMax = max;
      return pick;
    }
    __lovenDisplayPick = pick;
    __lovenDisplayPickMax = max;
    __lovenDisplayPickCommitted = true;
    return pick;
  }

  function resetInMemorySponsoredPick() {
    __lovenDisplayPick = null;
    __lovenDisplayPickMax = null;
    __lovenDisplayPickCommitted = false;
    __lovenLastFilterHash = null;
  }

  function restorePickFromStoredOrder(stored, eligibleCampaigns, max) {
    if (!stored) return null;
    if (stored.committedEmpty) return buildEmptySponsoredPick(max);
    if (!stored.ids || !stored.ids.length) return null;
    var order = stored.order && stored.order.length ? stored.order : null;
    var byHandle = {};
    var byId = {};
    (eligibleCampaigns || []).forEach(function (c) {
      if (c.productHandle) byHandle[normZoneHandle(c.productHandle)] = c;
      if (c.productId != null) byId[String(c.productId)] = c;
    });

    var campaigns = [];
    var ids = [];
    var handles = [];
    var used = {};
    var entries = order || [];
    var i;
    var entry;
    var c;
    var h;

    if (!entries.length && stored.ids) {
      stored.ids.forEach(function (id) {
        entries.push({ productId: id });
      });
    }

    for (i = 0; i < entries.length; i++) {
      entry = entries[i];
      c =
        (entry.productId != null && byId[String(entry.productId)]) ||
        (entry.productHandle && byHandle[normZoneHandle(entry.productHandle)]) ||
        null;
      if (!c || used[String(c.productId)]) continue;
      used[String(c.productId)] = true;
      campaigns.push(c);
      if (c.productId != null) ids.push(String(c.productId));
      h = normZoneHandle(c.productHandle);
      if (h) handles.push(h);
    }

    if (!campaigns.length) return null;
    return { ids: ids, handles: handles, campaigns: campaigns, order: order, max: max };
  }

  function reconcilePickWithEligible(pick, eligibleCampaigns, max) {
    if (isPickCommittedEmpty(pick)) {
      return buildEmptySponsoredPick(max);
    }
    if (!pick) return pickDisplaySponsoredSubset(eligibleCampaigns, max);
    var pseudo = {
      ids: pick.ids || [],
      handles: pick.handles || [],
      order: (pick.campaigns || []).map(function (c) {
        return { productId: c.productId, productHandle: c.productHandle };
      }),
    };
    var restored = restorePickFromStoredOrder(pseudo, eligibleCampaigns, max);
    if (restored && restored.campaigns.length) return restored;
    if (__lovenDisplayPickCommitted) {
      return buildEmptySponsoredPick(max);
    }
    return pickDisplaySponsoredSubset(eligibleCampaigns, max);
  }

  function isSearchSponsoredContext() {
    if (getCollectionHandleFromPath() === 'search') return true;
    return !!(document.body && document.body.classList.contains('template-search'));
  }

  /** Nombre total de résultats search (pas le seul viewport carte) pour le plafond 2 / 3. */
  function countResultsForSearchSponsoredCap() {
    if (typeof window.__lovenAvailabilityVisible === 'number' && window.__lovenAvailabilityVisible >= 0) {
      return window.__lovenAvailabilityVisible;
    }
    if (typeof window.__lovenGeoVisible === 'number' && window.__lovenGeoVisible >= 0) {
      return window.__lovenGeoVisible;
    }
    var wrap = document.getElementById('loven-collection-results-count');
    var dynWrap = document.getElementById('loven-dynamic-results-count');
    var serverCount = parseInt(
      (wrap && wrap.getAttribute('data-server-count')) ||
        (dynWrap && dynWrap.getAttribute('data-server-count')) ||
        '0',
      10
    );
    if (serverCount > 0) return serverCount;
    var root = document.getElementById('CollectionAjaxContent');
    if (!root) return 0;
    var n = 0;
    root.querySelectorAll('.grid-product[data-product-id]').forEach(function (card) {
      if (card.classList.contains('loven-geo-hidden')) return;
      if (card.style.display === 'none' || card.hidden) return;
      n++;
    });
    return n;
  }

  function getDisplaySponsoredMax() {
    if (!isSearchSponsoredContext()) {
      return COLLECTION_SPONSORED_MAX;
    }
    var count = countResultsForSearchSponsoredCap();
    return count >= SEARCH_SPONSORED_THRESHOLD
      ? SEARCH_SPONSORED_MAX_OVER
      : SEARCH_SPONSORED_MAX_UNDER;
  }

  function getNativeAdGridConfig(organicCount) {
    var max = getDisplaySponsoredMax();
    var slots =
      organicCount != null
        ? getSponsoredSlotPositions1Based(max, organicCount)
        : SPONSORED_GRID_SLOTS_1_BASED.slice(
            0,
            Math.min(max, SPONSORED_GRID_SLOTS_1_BASED.length)
          );
    return {
      maxBoostedPerPage: max,
      slots1Based: slots,
    };
  }

  /**
   * Retire les slots de moindre priorité (12 → 6 → …) si la grille organique
   * est trop courte pour atteindre la position (ex. 4 organiques → pas de slot @6).
   */
  function filterFeasibleSponsoredSlots(slots1Based, organicCount) {
    var slots = (slots1Based || []).slice();
    while (slots.length > 0) {
      if (slots[slots.length - 1] <= organicCount + slots.length) break;
      slots.pop();
    }
    return slots;
  }

  function getSponsoredSlotPositions1Based(maxSponsored, organicCount) {
    var wanted = SPONSORED_GRID_SLOTS_1_BASED.slice(
      0,
      Math.min(maxSponsored, SPONSORED_GRID_SLOTS_1_BASED.length)
    );
    return filterFeasibleSponsoredSlots(wanted, organicCount);
  }

  function buildGridWithHardcodedSlots(organicStream, sponsoredForSlots, slots1Based) {
    if (!sponsoredForSlots.length || !slots1Based.length) return organicStream.slice();
    var slotAt = {};
    var i;
    for (i = 0; i < slots1Based.length; i++) slotAt[slots1Based[i]] = true;

    var out = [];
    var oi = 0;
    var si = 0;
    var pos1Based = 1;
    while (oi < organicStream.length || si < sponsoredForSlots.length) {
      if (slotAt[pos1Based] && si < sponsoredForSlots.length) {
        out.push(sponsoredForSlots[si++]);
      } else if (oi < organicStream.length) {
        out.push(organicStream[oi++]);
      } else {
        break;
      }
      pos1Based++;
      if (out.length > 600) break;
    }
    return out;
  }

  window.LovenNativeAdGridConfig = getNativeAdGridConfig;
  window.LovenGetDisplaySponsoredMax = getDisplaySponsoredMax;
  window.LovenGetSponsoredGridSlots = getSponsoredSlotPositions1Based;

  function pickDisplaySponsoredSubset(campaigns, max) {
    max = max != null ? max : getDisplaySponsoredMax();
    var pool = orderedCampaignsForGrid(campaigns || []);
    if (!pool.length) return buildEmptySponsoredPick(max != null ? max : getDisplaySponsoredMax());
    var picked;
    if (!isSearchSponsoredContext() && pool.length <= max) {
      picked = pool.slice();
    } else {
      picked = shuffleArray(pool).slice(0, max);
    }
    var ids = [];
    var handles = [];
    picked.forEach(function (c) {
      if (c.productId != null) ids.push(String(c.productId));
      if (c.productHandle) handles.push(normZoneHandle(c.productHandle));
    });
    return { ids: ids, handles: handles, campaigns: picked };
  }

  function ensureDisplaySponsoredPick(eligibleCampaigns, opts) {
    opts = opts || {};
    var max = getDisplaySponsoredMax();
    var hash = buildActiveFiltersHash();
    var hashChanged = hash !== __lovenLastFilterHash;
    var eligible = eligibleCampaigns || [];

    if (hashChanged) {
      __lovenLastFilterHash = hash;
      __lovenDisplayPick = null;
      __lovenDisplayPickCommitted = false;
    }

    if (opts.reshuffle === true) {
      __lovenDisplayPickCommitted = false;
      __lovenDisplayPick = null;
      return finalizeSponsoredPick(pickDisplaySponsoredSubset(eligible, max), max);
    }

    if (__lovenDisplayPickCommitted && !hashChanged && __lovenDisplayPickMax === max) {
      if (
        isPickCommittedEmpty(__lovenDisplayPick) &&
        eligible.length &&
        !isZoneCollectionBrowsePage()
      ) {
        __lovenDisplayPickCommitted = false;
        return finalizeSponsoredPick(pickDisplaySponsoredSubset(eligible, max), max);
      }
      return finalizeSponsoredPick(
        reconcilePickWithEligible(__lovenDisplayPick, eligible, max),
        max
      );
    }

    if (!__lovenPartnerCampaignsSettled) {
      if (eligible.length) {
        return finalizeSponsoredPick(pickDisplaySponsoredSubset(eligible, max), max);
      }
      var provisionalEmpty = buildEmptySponsoredPick(max);
      __lovenDisplayPick = provisionalEmpty;
      __lovenDisplayPickMax = max;
      return provisionalEmpty;
    }

    // Un tirage vide PROVISOIRE (posé avant la réponse de /partner-campaigns,
    // typiquement par un événement collection:availability de la grille
    // dynamique de /collections/search) ne doit jamais être réconcilié comme
    // une décision : reconcilePickWithEligible() renverrait vide à vie, et
    // finalizeSponsoredPick() figerait ce vide maintenant que l'API a répondu.
    // Mesuré le 15/09/2026 : 4 éligibles, 0 affiché, 0 pastille sur la
    // recherche, alors qu'un simple re-tirage en produisait 2.
    if (
      __lovenDisplayPick &&
      !__lovenDisplayPickCommitted &&
      isPickCommittedEmpty(__lovenDisplayPick) &&
      eligible.length
    ) {
      __lovenDisplayPick = null;
      return finalizeSponsoredPick(pickDisplaySponsoredSubset(eligible, max), max);
    }

    if (__lovenDisplayPick && __lovenDisplayPickMax === max && !hashChanged) {
      return finalizeSponsoredPick(
        reconcilePickWithEligible(__lovenDisplayPick, eligible, max),
        max
      );
    }

    return finalizeSponsoredPick(pickDisplaySponsoredSubset(eligible, max), max);
  }

  function isCardSponsored(card) {
    if (!card) return false;
    var h = normZoneHandle(card.getAttribute('data-product-handle'));
    if (h && window.LovenSponsoredHandles instanceof Set && window.LovenSponsoredHandles.has(h)) {
      return true;
    }
    var id = card.getAttribute('data-product-id');
    if (id && window.LovenSponsored instanceof Set && window.LovenSponsored.has(String(id))) {
      return true;
    }
    return false;
  }

  function isRowSponsored(row) {
    if (!row) return false;
    var h = normZoneHandle(row && row.handle);
    if (h && window.LovenSponsoredHandles instanceof Set && window.LovenSponsoredHandles.has(h)) {
      return true;
    }
    return false;
  }

  function orderedCampaignsForGrid(campaigns) {
    var groups = {};
    var keys;
    var k;
    var out = [];
    (campaigns || []).forEach(function (c) {
      var p = Number(c.priorite) || 0;
      if (!groups[p]) groups[p] = [];
      groups[p].push(c);
    });
    keys = Object.keys(groups)
      .map(Number)
      .sort(function (a, b) {
        return b - a;
      });
    keys.forEach(function (prio) {
      out = out.concat(shuffleArray(groups[prio]));
    });
    return out;
  }

  function lovenGetGridProductWithTail(card) {
    var out = [card];
    var next = card.nextElementSibling;
    var pid = card.getAttribute('data-product-id');
    if (next && next.classList && next.classList.contains('modal--quick-shop') && pid) {
      if (String(next.getAttribute('data-product-id') || '') === String(pid)) {
        out.push(next);
      }
    }
    return out;
  }

  function findProductGridContainer() {
    var staticGrid = document.querySelector('.loven-static-collection-grid .new-grid.product-grid');
    var staticCount = staticGrid
      ? staticGrid.querySelectorAll(':scope > .grid-item.grid-product[data-product-id]').length
      : 0;
    var ajax = document.getElementById('CollectionAjaxResult');
    var dynHost = document.getElementById('loven-dynamic-availability');
    var dyn = null;
    if (
      ajax &&
      ajax.classList.contains('loven-dynamic-availability-mode') &&
      dynHost &&
      !dynHost.hasAttribute('hidden')
    ) {
      var dynWrap = dynHost.querySelector('.loven-dyn-grid:not(.loven-dyn-grid--skeleton)');
      if (dynWrap) dyn = dynWrap.querySelector('.new-grid.product-grid');
      if (dyn && dyn.querySelectorAll(':scope > .grid-item.grid-product[data-product-id]').length) {
        return dyn;
      }
    }
    if (staticCount > 0) return staticGrid;
    return staticGrid || dyn;
  }

  function getProductGridCards() {
    var grid = findProductGridContainer();
    if (!grid) return [];
    return Array.prototype.slice.call(
      grid.querySelectorAll(':scope > .grid-item.grid-product[data-product-id]')
    );
  }

  function isGridCardVisible(card) {
    return (
      !card.classList.contains('loven-geo-hidden') &&
      !card.classList.contains('loven-map-hidden') &&
      card.style.display !== 'none' &&
      !card.hidden
    );
  }

  /**
   * Pan/zoom : show/hide + masquées en fin de grille. Pas de re-ventilation
   * (les slots sont figés au tirage initial — un sponso qui entre dans le viewport
   * ne doit pas voler le slot #1).
   */
  function compactGridByMapVisibility() {
    var grid = findProductGridContainer();
    if (!grid) return;
    var cards = getProductGridCards();
    if (cards.length < 2) return;
    var visible = [];
    var hidden = [];
    cards.forEach(function (c) {
      if (isGridCardVisible(c)) visible.push(c);
      else hidden.push(c);
    });
    visible.concat(hidden).forEach(function (c) {
      lovenGetGridProductWithTail(c).forEach(function (node) {
        grid.appendChild(node);
      });
    });
  }

  function maintainGridOrderForMapViewport() {
    compactGridByMapVisibility();
    if (typeof window.LovenPaintSponsoredBadges === 'function') {
      window.LovenPaintSponsoredBadges();
    }
  }

  function getVisibleGridProductKeys() {
    var root = document.getElementById('CollectionAjaxContent');
    var ids = {};
    var handles = {};
    if (!root) return { ids: ids, handles: handles };
    root.querySelectorAll('.grid-product[data-product-id]').forEach(function (card) {
      if (!isGridCardVisible(card)) return;
      var id = card.getAttribute('data-product-id');
      var h = normZoneHandle(card.getAttribute('data-product-handle'));
      if (id) ids[String(id)] = true;
      if (h) handles[h] = true;
    });
    return { ids: ids, handles: handles };
  }

  function filterCampaignsToVisibleGrid(campaigns, keys) {
    return (campaigns || []).filter(function (c) {
      if (c.productId != null && keys.ids[String(c.productId)]) return true;
      var h = normZoneHandle(c.productHandle);
      return !!(h && keys.handles[h]);
    });
  }

  function resolveCampaignToCard(camp, byHandle, byId) {
    var card =
      (camp.productHandle && byHandle[normZoneHandle(camp.productHandle)]) ||
      (camp.productId != null && byId[String(camp.productId)]) ||
      null;
    if (!card || !isGridCardVisible(card)) return null;
    return card;
  }

  function ventilateProductGridDom(campaignOrder) {
    var grid = findProductGridContainer();
    if (!grid) return { boosted: 0, slots: [] };
    var maxSponsored = getDisplaySponsoredMax();

    var cards = Array.prototype.slice.call(
      grid.querySelectorAll(':scope > .grid-item.grid-product[data-product-id]')
    );
    if (cards.length < 2) return { boosted: 0, slots: [] };

    var byHandle = {};
    var byId = {};
    var visibleCards = [];
    var hiddenCards = [];
    var h;
    var id;
    var campaignHandles = {};
    var organicCount = 0;

    (campaignOrder || []).forEach(function (c) {
      if (c.productHandle) campaignHandles[normZoneHandle(c.productHandle)] = true;
    });

    cards.forEach(function (c) {
      h = c.getAttribute('data-product-handle');
      id = c.getAttribute('data-product-id');
      if (h) byHandle[normZoneHandle(h)] = c;
      if (id) byId[String(id)] = c;
      if (isGridCardVisible(c)) {
        visibleCards.push(c);
        if (!campaignHandles[normZoneHandle(h)]) organicCount++;
      } else {
        hiddenCards.push(c);
      }
    });

    var slots1Based = getSponsoredSlotPositions1Based(maxSponsored, organicCount);
    var slotCount = slots1Based.length;

    var pseudoRows = visibleCards.map(function (c) {
      return { handle: c.getAttribute('data-product-handle') || '' };
    });
    var ventilatedRows = ventilateAvailabilityRows(pseudoRows, campaignOrder);

    var boosted = 0;
    var boostedSet = {};
    (campaignOrder || []).forEach(function (camp) {
      if (boosted >= slotCount) return;
      var card = resolveCampaignToCard(camp, byHandle, byId);
      if (!card || boostedSet[card]) return;
      boostedSet[card] = true;
      boosted++;
    });

    ventilatedRows
      .map(function (row) {
        h = row && row.handle ? normZoneHandle(row.handle) : '';
        return h && byHandle[h] ? byHandle[h] : null;
      })
      .filter(Boolean)
      .concat(hiddenCards)
      .forEach(function (c) {
        lovenGetGridProductWithTail(c).forEach(function (node) {
          grid.appendChild(node);
        });
      });

    return {
      boosted: boosted,
      slots: slots1Based,
    };
  }

  function ventilateAvailabilityRows(rows, campaigns) {
    if (!rows || !rows.length || !campaigns || !campaigns.length) return rows;

    /* Ordre figé du tirage / __lovenSponsoredDisplayOrder — jamais re-shuffle au pan. */
    var order = (campaigns || []).slice();
    var rankByHandle = {};
    var forSlots = [];
    var forHandles = {};
    var organicStream = [];
    var i;
    var row;
    var h;
    var sponsRanked = [];
    var organicCount = 0;
    var maxSponsored = getDisplaySponsoredMax();

    order.forEach(function (c, idx) {
      if (c.productHandle) rankByHandle[normZoneHandle(c.productHandle)] = idx;
    });

    for (i = 0; i < rows.length; i++) {
      row = rows[i];
      h = normZoneHandle(row && row.handle);
      if (h && rankByHandle[h] != null) {
        sponsRanked.push({ row: row, rank: rankByHandle[h] });
      } else {
        organicCount++;
      }
    }

    sponsRanked.sort(function (a, b) {
      return a.rank - b.rank;
    });

    var slots1Based = getSponsoredSlotPositions1Based(maxSponsored, organicCount);
    var slotCount = slots1Based.length;

    for (i = 0; i < sponsRanked.length && forSlots.length < slotCount; i++) {
      row = sponsRanked[i].row;
      h = normZoneHandle(row && row.handle);
      if (!h || forHandles[h]) continue;
      forSlots.push(row);
      forHandles[h] = true;
    }

    for (i = 0; i < rows.length; i++) {
      row = rows[i];
      h = normZoneHandle(row && row.handle);
      if (!h || !forHandles[h]) organicStream.push(row);
    }

    return buildGridWithHardcodedSlots(organicStream, forSlots, slots1Based);
  }

  var boostGridTimer = null;
  function scheduleBoostSponsoredGrid(opts) {
    clearTimeout(boostGridTimer);
    boostGridTimer = setTimeout(function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          ventilateSponsoredGridNow(opts);
        });
      });
    }, 0);
  }

  function ventilateSponsoredGridNow(opts) {
    opts = opts || {};
    if (!opts.force && window.__lovenMapViewportFilterActive) return;
    var campaigns =
      window.__lovenSponsoredDisplayOrder && window.__lovenSponsoredDisplayOrder.length
        ? window.__lovenSponsoredDisplayOrder
        : window.__lovenDisplayCampaigns || window.__lovenEligibleCampaigns || [];
    if (!campaigns || !campaigns.length) return;
    var order =
      window.__lovenSponsoredDisplayOrder && window.__lovenSponsoredDisplayOrder.length
        ? window.__lovenSponsoredDisplayOrder
        : orderedCampaignsForGrid(campaigns);
    window.__lovenSponsoredGridOrder = order.map(function (c) {
      return c.productHandle || c.productId;
    });
    var result = ventilateProductGridDom(order);
    if (isAdsDebugOn()) {
      try {
        console.info(
          '[Loven sponsored] Grille —',
          result.boosted,
          'repositionné(s), slots 1-based:',
          result.slots
        );
        console.table(
          order.slice(0, result.slots.length).map(function (c, idx) {
            return {
              file_boost: idx + 1,
              handle: c.productHandle,
              priorite: c.priorite || 0,
              titre: c.heading || c.productTitle,
            };
          })
        );
      } catch (e) {}
    }
  }

  /**
   * Source unique : IDs Shopify + handles (grille API dyn-* / carte).
   */
  function publishSponsored(campaigns, opts) {
    opts = opts || {};
    var list = campaigns || [];
    window.__lovenAllEligibleCampaigns = list.slice();
    window.__lovenEligibleCampaigns = list.slice();

    var pick = ensureDisplaySponsoredPick(list, opts);
    var displayList = pick.campaigns || [];

    if (opts.reshuffle === true) {
      window.__lovenSponsoredDisplayOrder = orderedCampaignsForGrid(displayList);
    } else if (pick.order && pick.order.length) {
      window.__lovenSponsoredDisplayOrder = campaignsInStoredOrder(displayList, pick.order);
      displayList = campaignsInStoredOrder(displayList, pick.order);
    } else if (window.__lovenSponsoredDisplayOrder && window.__lovenSponsoredDisplayOrder.length) {
      displayList = campaignsInStoredOrder(displayList, window.__lovenSponsoredDisplayOrder);
    } else {
      window.__lovenSponsoredDisplayOrder = displayList.slice();
    }
    window.__lovenDisplayCampaigns = displayList;
    window.LovenSponsored = new Set(pick.ids);
    window.LovenSponsoredHandles = new Set(pick.handles);
    window.LovenSponsoredMapIds = new Set(pick.ids);
    window.LovenSponsoredMapHandles = new Set(pick.handles);
    document.dispatchEvent(
      new CustomEvent('loven:sponsored-ready', {
        detail: {
          ids: pick.ids,
          handles: pick.handles,
          campaigns: displayList,
          eligibleCount: list.length,
          mapIds: pick.ids,
          mapHandles: pick.handles,
        },
      })
    );
    if (typeof window.LovenPaintSponsoredBadges === 'function') {
      window.LovenPaintSponsoredBadges({
        ids: pick.ids,
        handles: pick.handles,
        campaigns: displayList,
      });
    }
    scheduleBoostSponsoredGrid({ force: true });
    logSponsoredDomDiagnostics(displayList);
  }

  function logSponsoredDomDiagnostics(campaigns) {
    var grid = findProductGridContainer();
    var list = campaigns || [];
    if (!list.length) return;
    if (!grid) {
      console.warn('[Loven sponsored] Grille produit introuvable pour pills / ventilation');
      return;
    }
    var byHandle = {};
    grid.querySelectorAll('.grid-item.grid-product[data-product-id]').forEach(function (card) {
      var h = card.getAttribute('data-product-handle');
      if (h) byHandle[normZoneHandle(h)] = card;
    });
    list.forEach(function (c) {
      var key = normZoneHandle(c.productHandle);
      if (!key) return;
      if (!byHandle[key]) {
        console.warn(
          '[Loven sponsored] Carte DOM absente pour',
          c.productHandle,
          '(titre:',
          c.productTitle || c.heading,
          ')'
        );
      }
    });
  }

  function logRegistrySummary(registry, campaigns) {
    var ctx = (registry && registry.context) || {};
    var handle = ctx.collectionHandle || '';
    var n = registry && registry.campaigns ? registry.campaigns.length : 0;
    var total = ctx.metaobjectTotal != null ? ctx.metaobjectTotal : n;
    var pages = ctx.metaobjectPages || 1;
    var scope = (ctx.collectionProductHandles || []).length;
    var elig = campaigns.length;
    var handles = campaigns.map(function (c) {
      return c.productHandle;
    });
    var source = ctx.source || (registry && registry.__backendFiltered ? 'supabase' : 'inline');
    var srcTag = '[' + source + ']';
    var header =
      '[Loven sponsored] ' +
      (handle || '(?)') +
      ' (id ' + (ctx.collectionId || '?') + ') ' + srcTag;
    console.groupCollapsed(
      header +
        ' — campagnes ' +
        n +
        (source !== 'supabase' && total != null && total !== n ? '/' + total : '') +
        (pages > 1 ? ' (page 1/' + pages + ')' : '') +
        ' · éligibles ' + elig
    );
    if (source === 'supabase') {
      console.log(
        'source : backend (filtrage produit ∈ collection ∧ zone côté serveur)',
        ctx.filtersApplied && ctx.filtersApplied.length ? '· filtres backend : ' + ctx.filtersApplied.join(', ') : ''
      );
      if (ctx.collectionProductCount) {
        console.log('produits collection (Shopify, sans limite) :', ctx.collectionProductCount);
      }
    } else if (scope) {
      console.log('produits collection (' + scope + ') :', ctx.collectionProductHandles || []);
    }
    console.log('campagnes éligibles (' + elig + ') :', handles);
    console.log(
      'plafond affichage :',
      getDisplaySponsoredMax(),
      isSearchSponsoredContext()
        ? '(search · ' + countResultsForSearchSponsoredCap() + ' résultats)'
        : '(collection)'
    );
    if (window.__lovenDisplayCampaigns && window.__lovenDisplayCampaigns.length !== elig) {
      console.log(
        'affichées « En vedette » (max ' +
          getDisplaySponsoredMax() +
          ') :',
        window.__lovenDisplayCampaigns.map(function (c) {
          return c.productHandle;
        })
      );
    }
    if (isAdsDebugOn() || (handle !== 'search' && elig === 0 && n > 0 && source !== 'supabase')) {
      var idx = handle === 'search' || source === 'supabase' ? null : buildEligibilityIndex(registry);
      console.table(
        (registry.campaigns || []).map(function (c) {
          var inScope = idx ? isProductInCollection(c, idx) : null;
          var zoneOk = idx ? zoneMatchesCurrentCollection(c, idx) : null;
          return {
            produit: c.productHandle,
            dansCollection: inScope === null ? (source === 'supabase' ? 'oui (backend)' : '—') : inScope ? 'oui' : 'non',
            zoneMatch: zoneOk === null ? (source === 'supabase' ? 'oui (backend)' : '—') : zoneOk ? 'oui' : 'non',
            zoneHandles: (c.zoneHandles || []).join(', '),
            zoneIds: (c.zoneIds || []).join(', '),
            eligible: source === 'supabase' ? 'OUI' : inScope && zoneOk ? 'OUI' : '—',
          };
        })
      );
    }
    console.groupEnd();
  }

  /**
   * Re-publie les sponsored à partir d'un registre (Supabase virtuel OU inline).
   * Si `registry` est omis : réutilise le dernier registre publié (cache) puis fallback DOM.
   */
  function publishSponsoredFromRegistry(opts, registry) {
    if (!registry) registry = __lovenLastRegistry || readRegistry();
    if (!registry) {
      console.warn(
        '[Loven sponsored] Registre introuvable (#loven-partner-ads-registry-json) — vérifier que la section « collection-top-ads » est bien dans le template courant.'
      );
      window.__lovenEligibleCampaigns = [];
      window.__lovenDisplayCampaigns = [];
      window.__lovenSponsoredDisplayOrder = [];
      __lovenDisplayPick = null;
      __lovenDisplayPickMax = null;
      __lovenLastFilterHash = null;
      __lovenDisplayPickCommitted = false;
      window.LovenSponsored = new Set();
      window.LovenSponsoredHandles = new Set();
      return;
    }
    __lovenLastRegistry = registry;
    var ctx = registry.context || {};
    var handle = ctx.collectionHandle || '';
    var campaigns = getEligibleCampaignsFromRegistry(registry, handle);
    publishSponsored(campaigns, opts);
    logRegistrySummary(registry, campaigns);
  }

  /**
   * Init : récupère le contexte du registre inline, puis :
   *  - /collections/search avec dest_lat/dest_lon : fetch /partner-campaigns mode GEO.
   *    Fallback registre inline si l'endpoint n'est pas (encore) étendu côté backend.
   *  - /collections/search sans geo : utilise l'inline (campagnes Liquid).
   *  - /collections/<X>            : fetch /partner-campaigns mode COLLECTION.
   *    Fallback registre inline (probablement vide → 0 sponso) si erreur.
   */
  function initAll() {
    __lovenPartnerCampaignsSettled = false;
    var inlineRegistry = readRegistry();
    if (!inlineRegistry) {
      console.warn(
        '[Loven sponsored] Registre introuvable au démarrage — bridge Supabase désactivé.'
      );
      return;
    }
    var ctx = inlineRegistry.context || {};
    var handle = ctx.collectionHandle || '';
    var id = ctx.collectionId != null ? ctx.collectionId : null;
    var isSearch = handle === 'search';

    function publishWithFallback(apiData, meta, modeLabel) {
      var registry;
      if (apiData && Array.isArray(apiData.campaigns)) {
        registry = buildVirtualRegistryFromApi(ctx, apiData);
      } else {
        registry = inlineRegistry;
        if (meta.error) {
          console.warn(
            '[Loven sponsored] Fetch partner-campaigns (' +
              modeLabel +
              ') en erreur (' +
              meta.error +
              ') — fallback registre inline.'
          );
        }
      }
      __lovenPartnerCampaignsSettled = true;
      publishSponsoredFromRegistry({}, registry);
      try {
        document.dispatchEvent(
          new CustomEvent('loven:partner-campaigns-ready', {
            detail: {
              source: meta.source,
              mode: modeLabel,
              count: (registry.campaigns || []).length,
              durationMs: meta.durationMs || 0,
              collectionHandle: handle,
              collectionId: id,
              error: meta.error || null,
            },
          })
        );
      } catch (e) {}
    }

    if (isSearch) {
      var dest = readDestSearchFromUrl();
      if (!dest) {
        __lovenPartnerCampaignsSettled = true;
        publishSponsoredFromRegistry({}, inlineRegistry);
        return;
      }
      fetchPartnerCampaignsGeo(dest.lat, dest.lng, dest.radiusKm, function (apiData, meta) {
        publishWithFallback(apiData, meta, 'geo');
      });
      return;
    }

    if (!handle) {
      __lovenPartnerCampaignsSettled = true;
      publishSponsoredFromRegistry({}, inlineRegistry);
      return;
    }

    fetchPartnerCampaigns(handle, id, function (apiData, meta) {
      publishWithFallback(apiData, meta, 'collection');
    });
  }

  function refreshSponsoredEligibility() {
    publishSponsoredFromRegistry({ reshuffle: false });
  }

  window.LovenRefreshSponsoredFromRegistry = refreshSponsoredEligibility;

  /**
   * Rule 4 — pan/zoom : show/hide uniquement, slots figés au tirage initial.
   */
  window.LovenRefreshSponsoredForMapViewport = maintainGridOrderForMapViewport;

  function maybeRefreshSponsoredVolumeCap() {
    var hash = buildActiveFiltersHash();
    var max = getDisplaySponsoredMax();
    if (hash === __lovenLastFilterHash && __lovenDisplayPickMax === max) return;
    var all = window.__lovenAllEligibleCampaigns;
    if (all && all.length) {
      publishSponsored(all, { reshuffle: false });
      return;
    }
    if (typeof window.LovenRefreshSponsoredFromRegistry === 'function') {
      window.LovenRefreshSponsoredFromRegistry();
    }
  }

  /**
   * Campagnes partenaires : réalignement geo quand la carte élargit le rayon (dézoom).
   * Collections zone (ex. calvados) : scope collection uniquement — pas de fetch geo au pan.
   */
  window.LovenRefetchPartnerCampaignsForGeo = function (lat, lon, radiusKm) {
    if (
      typeof window.LovenIsZoneCollectionGeoBrowse === 'function' &&
      window.LovenIsZoneCollectionGeoBrowse()
    ) {
      return;
    }
    var inlineRegistry = readRegistry();
    if (!inlineRegistry) return;
    fetchPartnerCampaignsGeo(lat, lon, radiusKm, function (apiData, meta) {
      var registry;
      if (apiData && Array.isArray(apiData.campaigns)) {
        registry = buildVirtualRegistryFromApi(inlineRegistry.context || {}, apiData);
      } else {
        registry = inlineRegistry;
        if (meta && meta.error) {
          console.warn(
            '[Loven sponsored] Fetch partner-campaigns (geo expand) en erreur — conservation du cache.'
          );
        }
      }
      publishSponsoredFromRegistry({ reshuffle: false }, registry);
    });
  };

  function runAfterCollectionGridPipeline() {
    scheduleBoostSponsoredGrid({ force: true });
  }

  document.addEventListener('DOMContentLoaded', function () {
    ensureCollectionUsesStaticGrid();
    initAll();
    runAfterCollectionGridPipeline();
    setTimeout(function () {
      ensureCollectionUsesStaticGrid();
      if (__lovenLastRegistry) {
        publishSponsoredFromRegistry({ reshuffle: false });
      }
    }, 300);
  });

  function refreshSponsoredGridVisibilityAfterMapPan() {
    if (typeof window.LovenPaintSponsoredBadges === 'function') {
      window.LovenPaintSponsoredBadges();
    }
  }

  document.addEventListener('collection:mapViewportFiltered', refreshSponsoredGridVisibilityAfterMapPan);
  document.addEventListener('collection:availability', function (e) {
    maybeRefreshSponsoredVolumeCap();
    var mapGeoExpand = e && e.detail && e.detail.mapGeoExpand;
    if (
      typeof window.LovenIsZoneCollectionGeoBrowse === 'function' &&
      window.LovenIsZoneCollectionGeoBrowse() &&
      mapGeoExpand
    ) {
      scheduleBoostSponsoredGrid({ force: true });
      return;
    }
    if (mapGeoExpand) {
      refreshSponsoredEligibility();
      scheduleBoostSponsoredGrid({ force: true });
      return;
    }
    if (window.__lovenMapViewportFilterActive) {
      if (typeof window.LovenPaintSponsoredBadges === 'function') {
        window.LovenPaintSponsoredBadges();
      }
      return;
    }
    refreshSponsoredEligibility();
    scheduleBoostSponsoredGrid({ force: true });
  });
  document.addEventListener('collection:destFiltered', function () {
    scheduleBoostSponsoredGrid({ force: true });
  });
  document.addEventListener('collection:reloaded', function () {
    initAll();
    if (typeof window.LovenBoostSponsoredGrid === 'function') {
      requestAnimationFrame(function () {
        window.LovenBoostSponsoredGrid({ force: true });
      });
    }
  });

  window.LovenReorderAvailabilityRowsForSponsored = function (rows) {
    var camps =
      window.__lovenSponsoredDisplayOrder && window.__lovenSponsoredDisplayOrder.length
        ? window.__lovenSponsoredDisplayOrder
        : window.__lovenDisplayCampaigns || window.__lovenEligibleCampaigns || [];
    return ventilateAvailabilityRows(rows, camps);
  };
  window.LovenBoostSponsoredGrid = ventilateSponsoredGridNow;
  window.LovenMaintainGridOrderForMapViewport = maintainGridOrderForMapViewport;
  window.LovenVentilateSponsoredGridForMapViewport = maintainGridOrderForMapViewport;
  window.LovenGetProductGridCards = getProductGridCards;

  /**
   * Helpers de debug — invocables depuis la console navigateur.
   *   LovenClearPartnerCampaignsCache()        → vide le cache pour la page courante (collection ou geo)
   *   LovenClearPartnerCampaignsCache('all')   → vide tout le cache loven:partner-campaigns:*
   *   LovenForceRefetchPartnerCampaigns()      → vide + relance initAll (campagnes fraîches)
   *   LovenGetLastPartnerCampaignsRegistry()   → renvoie le dernier registre publié
   */
  window.LovenClearPartnerCampaignsCache = function (scope) {
    try {
      if (scope === 'all' && window.sessionStorage) {
        var keys = [];
        var i;
        for (i = 0; i < window.sessionStorage.length; i++) {
          var k = window.sessionStorage.key(i);
          if (k && k.indexOf('loven:partner-campaigns:') === 0) {
            keys.push(k);
          }
        }
        keys.forEach(function (k) {
          window.sessionStorage.removeItem(k);
        });
        resetInMemorySponsoredPick();
        return keys.length;
      }
      var reg = __lovenLastRegistry || readRegistry();
      var ctx = (reg && reg.context) || {};
      var handle = ctx.collectionHandle || '';
      var id = ctx.collectionId || null;
      if (handle === 'search') {
        var dest = readDestSearchFromUrl();
        if (dest) {
          clearPartnerCampaignsCacheByPayload({
            dest_lat: dest.lat,
            dest_lon: dest.lng,
            dest_radius_km: dest.radiusKm,
          });
          return 1;
        }
        return 0;
      }
      clearPartnerCampaignsCacheByPayload({
        collection_handle: handle,
        collection_id: id != null ? String(id) : '',
      });
      return 1;
    } catch (e) {
      return 0;
    }
  };
  window.LovenForceRefetchPartnerCampaigns = function () {
    window.LovenClearPartnerCampaignsCache();
    resetInMemorySponsoredPick();
    initAll();
  };
  window.LovenGetLastPartnerCampaignsRegistry = function () {
    return __lovenLastRegistry;
  };
  window.LovenGetSponsoredFiltersHash = buildActiveFiltersHash;
  window.LovenClearSponsoredPickCache = resetInMemorySponsoredPick;
})();
